import React from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { getFirstItemFrom } from "../../../utils/utils";
import { decryptIfEncrypted } from "../../../utils/fieldDecryptor";
import { setStudent } from "../../../store/slices/b2b/b2bContracts.slice";
import Placeholder from "../../../components/Placeholder/PlaceholderByPage";

import { TableUser } from "../companies/styles";

function StudentsList({ setShowStudentReport, setShowAddStudentForm }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { students, contract } = useSelector((state) => state.b2bContractsReducer);

  const getStudentFrom = (item) => {
    const student = item.students[0];
    const studentPlans = getFirstItemFrom(student.studentPlans);
    const studentB2b = getFirstItemFrom(student.studentB2bs);
    const contractStudent = getFirstItemFrom(student.contractStudents);
    const userEmails = item.userEmails.map((l) => {
      return { id: l.id, email: l.email.toLowerCase(), notify: l.notify };
    });
    const userPhones = item.userPhones.map((p) => {
      return { id: p.id, userPhoneTypeId: p.userPhoneTypeId, phone: p.phone };
    });
    const studentLanguages = student.studentLanguages.map((l) => {
      return { languageId: l.languageId, isNative: l.isNative };
    });
    const contractPlanStudent = getFirstItemFrom(student.contractPlanStudents);

    let studentPlanLanguages = [];
    if (studentPlans.studentPlanLanguages && studentPlans.studentPlanLanguages.length > 0) {
      studentPlanLanguages = studentPlans.studentPlanLanguages.map((l) => {
        let studentLanguageLevelId,
          studentLanguageLevel = undefined;
        if (student.studentLevelGrades) {
          const levelBylanguage = student.studentLevelGrades.filter((lg) => lg.lingoLanguageId === l.lingoLanguageId);
          if (levelBylanguage.length > 0) {
            studentLanguageLevelId = levelBylanguage[0].id;
            studentLanguageLevel = levelBylanguage[0].levelId;
          }
        }

        return {
          id: l.id,
          lingoLanguageId: l.lingoLanguageId,
          focus: l.focus,
          struct: l.struct,
          studentLanguageLevelId: studentLanguageLevelId,
          studentLanguageLevel: studentLanguageLevel,
        };
      });
    } else {
      studentPlanLanguages = [
        {
          lingoLanguageId: "",
          focus: "traditional",
          struct: "balanced",
          studentLanguageLevel: "",
        },
      ];
    }

    return {
      id: item.id,
      name: item.name,
      email: item.email.toLowerCase(),
      picture: item.picture,
      userEmails,
      userPhones,
      address: item.address,
      countryId: item.countryId,
      gender: item.gender,
      birthDate: item.birthDate,
      timezone: item.timezone,
      active: item.active,
      typeLanguage: item.students[0].typeTeacherPreference,
      studentId: student.id,
      studentPlanId: studentPlans.id,
      classTool: student.classTool ? student.classTool : "",
      classToolId: student.classToolId ? student.classToolId : "",
      studentPlanLanguages,
      studentLanguages,
      students: student,
      studentB2b: {
        id: studentB2b.id,
        allowBuyExtraClasses: studentB2b.allowBuyExtraClasses,
        allowChangeCourseFocus: studentB2b.allowChangeCourseFocus,
        allowChangeCourseStruct: studentB2b.allowChangeCourseStruct,
        allowChangeClassFocus: studentB2b.allowChangeClassFocus,
        allowChangeLingoPlan: studentB2b.allowChangeLingoPlan,
        targetClassesPerMonth: studentB2b.targetClassesPerMonth,
        observation: studentB2b.observation,
      },
      contractPlanId: contractPlanStudent.contractPlanId, // POST
      contractPlanStudent: {
        id: contractPlanStudent.id,
        contractPlanId: contractPlanStudent.contractPlanId,
      },
      contractStudentId: contractStudent.id,
    };
  };

  const setSelected = (item) => {
    if (item) {
      const studentReq = getStudentFrom(item);

      const newStudent = {
        ...studentReq,
        contractId: contract.id,
        name: decryptIfEncrypted(studentReq.name),
        email: decryptIfEncrypted(studentReq.email),
        gender: decryptIfEncrypted(studentReq.gender),
        address: decryptIfEncrypted(studentReq.address),
        birthDate: decryptIfEncrypted(studentReq.birthDate),
        userEmails: studentReq.userEmails.map((item) => ({ ...item, email: decryptIfEncrypted(item.email) })),
        userPhones: studentReq.userPhones.map((item) => ({ ...item, phone: decryptIfEncrypted(item.phone) })),
      };

      dispatch(setStudent(newStudent));
    }

    setShowAddStudentForm(true);
  };

  const showModal = (item) => {
    setShowStudentReport(true);
    dispatch(setStudent(item));
  };

  const items = students || [];

  return (
    <TableUser>
      <div>
        <div className="bigBox">
          <table>
            <thead>
              <tr>
                <th>{t("NAME")}</th>
                <th>{t("COUNTRY")}</th>
                <th>{t("SUBSCRIPTION_DATE")}</th>
                <th>{t("RESETS_ON")}</th>
                <th>{t("PLAN")}</th>
                <th style={{ textAlign: "center" }}> {t("STATUS")}</th>
                <th style={{ textAlign: "center" }}> {t("OPTIONS")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                let nextCycle = "";
                if (
                  item.students.length &&
                  item.students[0].contractPlanStudents.length &&
                  item.students[0].contractPlanStudents[0].studentPlanB2BRenewalCycle
                ) {
                  nextCycle = moment(item.students[0].contractPlanStudents[0].studentPlanB2BRenewalCycle.nextCy)
                    .utc()
                    .format(t("DATE_FORMAT"));
                }
                const status = item.students[0]?.studentPlans[0]?.active;

                return (
                  <tr key={item.id}>
                    <td>{decryptIfEncrypted(item.name)}</td>
                    <td>{decryptIfEncrypted(item.country.name)}</td>
                    <td>{moment(item.createdAt).utc().format(t("DATE_FORMAT"))}</td>
                    <td>{nextCycle}</td>
                    <td>{item.students[0].studentPlans[0].plan.nameEnglish}</td>
                    <td>
                      <span className={!status ? "inativo" : ""}>{status ? "Active" : "Inactive"}</span>
                    </td>
                    <td>
                      <button type="button" onClick={() => setSelected(item)}>
                        {t("BTN_VIEW")} <i className="fa fa-angle-right" aria-hidden="true"></i>
                      </button>
                      <button type="button" onClick={() => showModal(item)}>
                        {t("REPORT")} <i className="fa fa-angle-right" aria-hidden="true"></i>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {items.length === 0 && (
        <div style={{ paddingTop: "50px" }}>
          <Placeholder pageName="users" textMsg="Nothing here yet" />
        </div>
      )}
    </TableUser>
  );
}

export default StudentsList;
